/**
 * Alta y edición de un KPI del catálogo.
 *
 * Valida con el **mismo** schema que valida el servidor
 * (`upsertMetricDefinitionSchema`), así el error de "una métrica necesita al
 * menos un umbral" aparece antes del round-trip y con las mismas palabras. Si la
 * escritura falla, el diálogo no se cierra: `Modal` ya trae el camino de error
 * con Reintentar y los datos quedan intactos.
 */
import {
  SOURCE_LABELS,
  sourceKindSchema,
  unitSchema,
  upsertMetricDefinitionSchema,
  type MetricDefinition,
  type SourceKind,
  type Unit,
  type UpsertMetricDefinitionInput,
  type User,
} from '@ops/contracts'
import { useEffect, useState } from 'react'

import { mensajeDeError } from '../../lib/api'
import { useToast } from '../../shell/AppShell'
import { Button, Modal, SelectField, Stack, TextField } from '../../ui'
import {
  DIRECCION_LABELS,
  DIRECCION_OPERADOR,
  UNIDAD_LABELS,
  type Direccion,
} from './etiquetas'
import { useUpsertMetricDefinition } from './queries'

const SIN_DUENO = ''

interface Borrador {
  slug: string
  nombre: string
  definicion: string
  formula: string
  unidad: Unit
  benchmark: string
  direccion: Direccion
  umbralOk: string
  umbralWarning: string
  source: SourceKind
  ownerId: string
}

const VACIO: Borrador = {
  slug: '',
  nombre: '',
  definicion: '',
  formula: '',
  unidad: 'count',
  benchmark: '',
  direccion: 'higher_better',
  umbralOk: '',
  umbralWarning: '',
  source: 'manual',
  ownerId: SIN_DUENO,
}

function borradorDe(metrica: MetricDefinition): Borrador {
  return {
    slug: metrica.slug,
    nombre: metrica.nombre,
    definicion: metrica.definicion,
    formula: metrica.formula ?? '',
    unidad: metrica.unidad,
    benchmark: metrica.benchmark ?? '',
    direccion: metrica.direccion,
    umbralOk: metrica.umbralOk === null ? '' : String(metrica.umbralOk),
    umbralWarning:
      metrica.umbralWarning === null ? '' : String(metrica.umbralWarning),
    source: metrica.source,
    ownerId: metrica.ownerId ?? SIN_DUENO,
  }
}

/** `1.234` y `1.234.567`: punto seguido de grupos de tres, sin coma decimal. */
const MILES_ES_AR = /^-?\d{1,3}(\.\d{3})+$/

/**
 * Lee un umbral escrito en es-AR sin adivinar de más.
 *
 * `''` → null (un umbral vacío es "sin umbral", nunca cero) · `'1.234,5'` →
 * 1234.5 · `'75.000'` → 75000 · `'0.5'` → 0.5 · basura → NaN, que el schema
 * rechaza con las mismas palabras que usaría el servidor.
 *
 * El caso peligroso es `'75.000'`: leerlo como 75 pondría el semáforo de MRR mil
 * veces más abajo sin que nadie lo note. Por eso el punto se trata como separador
 * de miles solo cuando la forma es inequívocamente de miles, y el campo es de
 * texto y no `type="number"` — así el navegador no impone otra interpretación.
 */
export function numeroDeCampo(texto: string): number | null {
  const limpio = texto.trim()
  if (limpio === '') return null
  if (limpio.includes(',')) {
    return Number(limpio.replace(/\./g, '').replace(',', '.'))
  }
  if (MILES_ES_AR.test(limpio)) return Number(limpio.replace(/\./g, ''))
  return Number(limpio)
}

function texto(valor: string): string | null {
  const limpio = valor.trim()
  return limpio === '' ? null : limpio
}

export interface MetricaDialogProps {
  open: boolean
  /** `null` = alta. Con métrica, edición: el slug queda fijo. */
  metrica: MetricDefinition | null
  /** Catálogo completo, para no dar de alta un slug que ya existe. */
  metricas: readonly MetricDefinition[]
  usuarios: readonly User[]
  onClose: () => void
}

export function MetricaDialog({
  open,
  metrica,
  metricas,
  usuarios,
  onClose,
}: MetricaDialogProps) {
  const { push } = useToast()
  const mutation = useUpsertMetricDefinition()
  const [borrador, setBorrador] = useState<Borrador>(VACIO)
  const [errores, setErrores] = useState<Record<string, string>>({})

  const esAlta = metrica === null

  // Cada apertura arranca del registro que se está editando, no de lo que quedó
  // tipeado la vez anterior.
  useEffect(() => {
    if (!open) return
    setBorrador(metrica === null ? VACIO : borradorDe(metrica))
    setErrores({})
    mutation.reset()
    // `mutation` es estable por render de react-query; depender de él acá
    // reabriría el efecto en cada mutación.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, metrica])

  function set<K extends keyof Borrador>(campo: K, valor: Borrador[K]) {
    setBorrador((actual) => ({ ...actual, [campo]: valor }))
  }

  function guardar() {
    const candidato = {
      slug: borrador.slug.trim(),
      nombre: borrador.nombre.trim(),
      definicion: borrador.definicion.trim(),
      formula: texto(borrador.formula),
      unidad: borrador.unidad,
      benchmark: texto(borrador.benchmark),
      direccion: borrador.direccion,
      umbralOk: numeroDeCampo(borrador.umbralOk),
      umbralWarning: numeroDeCampo(borrador.umbralWarning),
      source: borrador.source,
      ownerId: borrador.ownerId === SIN_DUENO ? null : borrador.ownerId,
    }

    const parsed = upsertMetricDefinitionSchema.safeParse(candidato)
    if (!parsed.success) {
      const mapa: Record<string, string> = {}
      for (const issue of parsed.error.issues) {
        const campo = issue.path.join('.') || 'slug'
        mapa[campo] ??= issue.message
      }
      setErrores(mapa)
      return
    }

    // El PUT es un upsert: en un alta, un slug repetido sobreescribiría la
    // métrica existente sin avisar. Se corta acá.
    if (esAlta && metricas.some((m) => m.slug === parsed.data.slug)) {
      setErrores({
        slug: `Ya existe una métrica con el slug ${parsed.data.slug}. Editala en vez de duplicarla.`,
      })
      return
    }

    setErrores({})
    enviar(parsed.data)
  }

  function enviar(input: UpsertMetricDefinitionInput) {
    mutation.mutate(input, {
      onSuccess: (guardada) => {
        push(
          'ok',
          esAlta
            ? `Se dio de alta ${guardada.nombre} en el catálogo`
            : `Se guardó ${guardada.nombre}`,
        )
        onClose()
      },
    })
  }

  const opcionesDueno = [
    { value: SIN_DUENO, label: 'Sin dueño asignado' },
    ...usuarios
      .filter((u) => u.activo)
      .map((u) => ({ value: u.id, label: u.nombre })),
  ]

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={esAlta ? 'Agregar un KPI al catálogo' : `Editar ${metrica.nombre}`}
      subtitle={
        esAlta
          ? 'Queda disponible en todo el panel sin deploy: la métrica es un registro.'
          : `Slug ${metrica.slug}. Cambiar los umbrales cambia el semáforo en todas las pantallas; los datos ya cargados no se tocan.`
      }
      error={mutation.isError ? mensajeDeError(mutation.error) : null}
      onRetry={mutation.isError ? guardar : undefined}
      reintentando={mutation.isPending}
      footer={
        <>
          <Button variante="secundario" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variante="primario"
            onClick={guardar}
            cargando={mutation.isPending}
          >
            {esAlta ? 'Dar de alta' : 'Guardar cambios'}
          </Button>
        </>
      }
    >
      <Stack gap="md">
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label="Slug"
            requerido
            value={borrador.slug}
            onChange={(v) => set('slug', v)}
            disabled={!esAlta}
            placeholder="tiempo_activacion"
            ayuda={
              esAlta
                ? 'Minúsculas, números y guion bajo. Es la clave del KPI y no se puede cambiar después.'
                : 'El slug es la clave del registro: no se edita.'
            }
            error={errores.slug}
          />
          <TextField
            label="Nombre"
            requerido
            value={borrador.nombre}
            onChange={(v) => set('nombre', v)}
            placeholder="Tiempo de activación"
            ayuda="Como se lee en las tarjetas y las tablas."
            error={errores.nombre}
          />
        </div>

        <TextField
          label="Definición"
          requerido
          filas={3}
          value={borrador.definicion}
          onChange={(v) => set('definicion', v)}
          placeholder="Qué mide exactamente y qué queda afuera."
          ayuda="Entre 10 y 500 caracteres. Es lo que evita que dos personas discutan un número midiendo cosas distintas."
          error={errores.definicion}
        />

        <TextField
          label="Fórmula"
          value={borrador.formula}
          onChange={(v) => set('formula', v)}
          placeholder="MRR × 12"
          ayuda="Se publica al lado del número para poder recalcularlo a mano. Vacío si el dato es crudo y no se deriva de otro."
          error={errores.formula}
        />

        <div className="grid grid-cols-2 gap-3">
          <SelectField
            label="Unidad"
            requerido
            value={borrador.unidad}
            onChange={(v) => set('unidad', unitSchema.parse(v))}
            opciones={unitSchema.options.map((u) => ({
              value: u,
              label: UNIDAD_LABELS[u],
            }))}
            ayuda="Define el formato: dólares sin decimales, porcentaje con coma."
            error={errores.unidad}
          />
          <SelectField
            label="Dirección"
            requerido
            value={borrador.direccion}
            onChange={(v) =>
              set(
                'direccion',
                v === 'lower_better' ? 'lower_better' : 'higher_better',
              )
            }
            opciones={(['higher_better', 'lower_better'] as const).map((d) => ({
              value: d,
              label: DIRECCION_LABELS[d],
            }))}
            ayuda="Sin esto un delta no sabe si crecer está bien (MRR) o mal (churn)."
            error={errores.direccion}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <TextField
            label={`Umbral en target (${DIRECCION_OPERADOR[borrador.direccion]})`}
            inputMode="decimal"
            value={borrador.umbralOk}
            onChange={(v) => set('umbralOk', v)}
            placeholder="75.000"
            ayuda="Desde acá la métrica está en verde. Formato es-AR: 75.000 o 75000."
            error={errores.umbralOk}
          />
          <TextField
            label={`Umbral de atención (${DIRECCION_OPERADOR[borrador.direccion]})`}
            inputMode="decimal"
            value={borrador.umbralWarning}
            onChange={(v) => set('umbralWarning', v)}
            placeholder="65.000"
            ayuda="Entre este y el de target, ámbar. Peor que este, rojo."
            error={errores.umbralWarning}
          />
        </div>

        <TextField
          label="Benchmark"
          value={borrador.benchmark}
          onChange={(v) => set('benchmark', v)}
          placeholder=">3× saludable"
          ayuda="La referencia de la industria, en palabras. Se muestra en el detalle de la métrica."
          error={errores.benchmark}
        />

        <div className="grid grid-cols-2 gap-3">
          <SelectField
            label="Fuente declarada"
            requerido
            value={borrador.source}
            onChange={(v) => set('source', sourceKindSchema.parse(v))}
            opciones={sourceKindSchema.options.map((s) => ({
              value: s,
              label: SOURCE_LABELS[s],
            }))}
            ayuda="De dónde va a salir el dato. Un datapoint cargado a mano igual se marca como manual."
            error={errores.source}
          />
          <SelectField
            label="Dueño"
            value={borrador.ownerId}
            onChange={(v) => set('ownerId', v)}
            opciones={opcionesDueno}
            ayuda="Quién responde por que este número esté cargado."
            error={errores.ownerId}
          />
        </div>
      </Stack>
    </Modal>
  )
}
